import { EdiphiReportTemplate, VisibilityContext, ediphiReportTemplates } from "../../excel/reports/templates/registry";
import { fetchEstimateExport } from "../../api";
import { fetchBidTabsWithScopeData } from "../../excel/reports/contractingSOV";
import { fetchGCGRReportAvailability } from "../../excel/reports/gcgr";
import { fetchMarkupItemsForReport } from "../../excel/reports/markups";
import {
  setCalculationManual,
  getEstimateReportData,
  getProjectReportData,
  getGCGRReportData,
  getContractingReportData,
} from "../../excel/workbook";
import { refreshEstimateDatasource } from "../../excel/datasources/estimate";
import { refreshContractingDatasource } from "../../excel/datasources/bidTabs";
import { refreshScopeSheetCache } from "../../api/bids";
import {
  ReportStaleCheck,
  contractingDefaultChanged,
  estimateDefaultChanged,
  gcgrDefaultChanged,
  reportChecksChanged,
} from "../../excel/datasources/staleness";
import { getLoadedContractingProjectId, getLoadedGcgrId } from "../../excel/workbook/state";
import { refreshGCGRCaches } from "../../api/gcgrs";
import Logger from "../../clients/sentry";
import { hasSession } from "../../utils/session";
import { DatasourceGoneError, handleError, serializeErrorForWarning } from "../../errors";
import { useProject } from "../context/ProjectContext";
import { useDatasources } from "../context/DatasourceContext";
import { ValidationError } from "../../errors";
import { LoggingInfo } from "./types";
import { EstimateReport } from "../../interfaces/EstimateReport";
import { GCGRReport } from "../../interfaces/GCGRReport";
import { ContractingReport } from "../../interfaces/ContractingReport";
import { ReportGroup } from "../../excel/reports/api/groups";
import { CONTRACTING_GONE_MESSAGE, ESTIMATE_GONE_MESSAGE, GCGR_GONE_MESSAGE } from "../constants";
import { AREA_SHEET_REPORT_NAME, ROOMS_REPORT_NAME, UNIT_MIX_REPORT_NAME } from "../../excel/reports/api/reportNames";
import {
  tabulateContractingReportData,
  tabulateEstimateReportData,
  tabulateGCGRReportData,
  tabulateProjectReportData,
} from "../../excel/datasources/reportTables";
import { reportTypesExist } from "../../excel/reports/api/operations";
import { setWorksheetOption } from "../../excel/workbook";

export interface ReportDataStaleness {
  projectId: string;
  estimateId?: string;
  gcgrId?: string;
  needsEstimateRefresh: boolean;
  needsGCGRRefresh: boolean;
  needsContractingRefresh: boolean;
  /** Scope-sheet edits bump no timestamp, so a contracting report's refresh always refetches it. */
  needsScopeRefresh: boolean;
  needsRefresh: boolean;
}

export function useReportActions() {
  const { setSelectedEstimate, setSelectedGcgr } = useProject();
  const { markDataRefreshed } = useDatasources();

  const withReportLogging = async (callback: () => Promise<any>, loggingInfo?: LoggingInfo) => {
    try {
      await callback();
    } catch (error) {
      const err = handleError(error, {
        context: loggingInfo?.context || "withReportLogging",
        reportType: loggingInfo?.reportType,
        worksheetName: loggingInfo?.worksheetName,
      });
      const message = loggingInfo?.message || "Error generating report";
      // Signed out, every request fails for want of a token; the sign-in screen covers that.
      if (hasSession()) Logger.error(message, err);
      throw err;
    }
  };

  const changeReportOption = async (
    worksheetName: string,
    key: string,
    value: boolean | string | string[],
    isTemplate = false
  ) => {
    try {
      await setWorksheetOption(worksheetName, key, value, isTemplate);
    } catch (error) {
      const err = handleError(error, { worksheetName, key, value, isTemplate, context: "changeReportOption" });
      Logger.error("Change report option failed", err);
      throw err;
    }
  };

  // Compare each report row's saved server updated_at with the server's. A failed probe must not
  // block report generation — warn and skip that refresh so the report runs with the data
  // already in the workbook (report generation has no hard dependency on these endpoints).
  const checkReportDataStaleness = async (
    /** Probe only this datasource. A report answers for its own data, not for the whole workbook. */
    scope?: ReportGroup,
    staleChecks: ReportStaleCheck[] = []
  ): Promise<ReportDataStaleness | null> => {
    // TODO: Investigate in a follow up PR, if we can safely bring in promise.all into this function without breaking the Excel.run context.
    const inScope = (type: ReportGroup) => !scope || scope === type;
    const projectReportRows = await getProjectReportData();
    const estimateReportRows = await getEstimateReportData();
    const gcgrReportRows = await getGCGRReportData();
    const contractingReportRows = await getContractingReportData();

    const estimateReport = (estimateReportRows as EstimateReport[])[0];
    const gcgrReport = (gcgrReportRows as GCGRReport[])[0];
    const contractingReport = (contractingReportRows as ContractingReport[])[0];
    const projectId = (projectReportRows[0]?.project_id ??
      estimateReport?.project_id ??
      gcgrReport?.project_id ??
      contractingReport?.project_id) as string;
    if (!projectId) return null;

    const estimateId = estimateReport?.estimate_id as string | undefined;
    // getGCGRReportData handles the legacy __GCGR fallback for pre-migration workbooks
    const gcgrId = gcgrReport?.gcgr_id as string | undefined;
    // The three probes are pure API calls (no Excel.run), so they can run concurrently —
    // only the table reads above must stay serial
    const [needsEstimateRefresh, needsGCGRRefresh, needsContractingRefresh] = await Promise.all([
      (async () => {
        if (!inScope("estimate") || !estimateId) return false;
        try {
          const [defaultChanged, reportChanged] = await Promise.all([
            estimateDefaultChanged(estimateId, estimateReport),
            reportChecksChanged("estimate", staleChecks, { projectId, estimateId }, estimateReport),
          ]);
          return defaultChanged || reportChanged;
        } catch (error) {
          Logger.warn("Estimate staleness check failed, skipping refresh", serializeErrorForWarning(error));
          return false;
        }
      })(),
      (async () => {
        if (!inScope("gcgr") || !gcgrId) return false;
        try {
          const [defaultChanged, reportChanged] = await Promise.all([
            gcgrDefaultChanged(projectId, gcgrId, gcgrReport),
            reportChecksChanged("gcgr", staleChecks, { projectId, gcgrId }, gcgrReport),
          ]);
          return defaultChanged || reportChanged;
        } catch (error) {
          Logger.warn("GCGR staleness check failed, skipping refresh", serializeErrorForWarning(error));
          return false;
        }
      })(),
      (async () => {
        if (!inScope("contracting") || !contractingReport?.project_id) return false;
        try {
          return await contractingDefaultChanged(projectId, contractingReport);
        } catch (error) {
          Logger.warn("Contracting staleness check failed, skipping refresh", serializeErrorForWarning(error));
          return false;
        }
      })(),
    ]);

    const needsScopeRefresh = scope === "contracting" && !!contractingReport?.project_id && !needsContractingRefresh;

    return {
      projectId,
      estimateId,
      gcgrId,
      needsEstimateRefresh,
      needsGCGRRefresh,
      needsContractingRefresh,
      needsScopeRefresh,
      needsRefresh: needsEstimateRefresh || needsGCGRRefresh || needsContractingRefresh || needsScopeRefresh,
    };
  };

  // Refresh the report tables flagged stale by checkReportDataStaleness. Fresh data is fetched
  // via the refresh* helpers (swap caches on success, keep the cached copy on failure) and a
  // failed refetch THROWS — the run aborts with the standard error banner (main behavior) and
  // the report row isn't rewritten, so the next click retries. A datasource the server no longer
  // has throws DatasourceGoneError and leaves its caches and report table as they were.
  const refreshReportData = async (staleness: ReportDataStaleness): Promise<void> => {
    const { projectId, estimateId, gcgrId } = staleness;

    // Each branch below swaps its cache before tabulating .metadata, so a tabulation failure leaves
    // the cache one revision ahead of metadata — a bounded, self-correcting freshness skew (both
    // remain present; .metadata is the loaded-signal, and the next staleness retry re-tabulates).

    // Any datasource refresh writes the project row first; legacy __Report is rebuilt from it.
    if (staleness.needsEstimateRefresh || staleness.needsGCGRRefresh || staleness.needsContractingRefresh) {
      await tabulateProjectReportData(projectId);
    }

    if (staleness.needsEstimateRefresh && estimateId) {
      const exportResult = await refreshEstimateDatasource(projectId, estimateId);
      if (!exportResult.success) throw exportResult.error;
      // An empty-success payload never swaps the cache — proceeding would re-tabulate the
      // OLD memoized data with fresh stamps, masking the change. The export answered,
      // so an empty one means the estimate is gone rather than the endpoint being unreachable.
      if (!exportResult.data) throw new DatasourceGoneError(ESTIMATE_GONE_MESSAGE);
      if (exportResult.data.estimate) setSelectedEstimate(exportResult.data.estimate);
      await tabulateEstimateReportData(exportResult.data);
    }

    if (staleness.needsGCGRRefresh && gcgrId) {
      // Refresh the GCGR the probe fired for, never whichever sorts first — rebinding the report
      // row to a different GCGR would silently change what the workbook reports on.
      const refreshResult = await refreshGCGRCaches(projectId, gcgrId);
      if (!refreshResult.success) throw refreshResult.error;
      const gcgr = refreshResult.data;
      if (gcgr) {
        await tabulateGCGRReportData(projectId, gcgr);
        setSelectedGcgr(gcgr);
      } else {
        // Gone from the project. A report refresh answers for its own report and nothing else, so it
        // refuses rather than clearing the workbook or the card — that is the datasource path's call,
        // and it asks before removing anything. Throwing here also stops the report regenerating empty
        // over content that is still good.
        throw new DatasourceGoneError(GCGR_GONE_MESSAGE);
      }
    }

    if (staleness.needsContractingRefresh) {
      // Scope keeps its cached fallback on failure — it is non-critical in getBidTabs
      const bidGroupsResult = await refreshContractingDatasource(projectId);
      if (!bidGroupsResult.success) throw bidGroupsResult.error;
      // No bid packages is contracting's version of a deleted GCGR, and gets the same treatment: refuse
      // rather than clear. A report refresh answers for its own report and leaves the datasource alone.
      if (!bidGroupsResult.data?.bidGroups?.length) throw new DatasourceGoneError(CONTRACTING_GONE_MESSAGE);
      await tabulateContractingReportData(projectId, bidGroupsResult.data.bidGroups);
    } else if (staleness.needsScopeRefresh) {
      // Non-critical in getBidTabs, so a failed refetch keeps the cached copy rather than aborting
      await refreshScopeSheetCache(projectId);
    }
    // Cards read their saved summaries, so this is what tells them a row moved.
    markDataRefreshed();
  };

  const getVisibleTemplates = async (): Promise<Record<string, EdiphiReportTemplate>> => {
    // Each datasource answers for itself. Sourcing the project id from ProjectReport rather than
    // EstimateReport is what lets GCGR and contracting templates appear with no estimate loaded,
    // and gating each fetch on its own datasource skips the round-trips for absent ones.
    const projectId = (await getProjectReportData())[0]?.project_id as string | undefined;
    const estimateId = (await getEstimateReportData())[0]?.estimate_id as string | undefined;
    const gcgrId = await getLoadedGcgrId();
    const hasContracting = (await getLoadedContractingProjectId()) === projectId;

    // One at a time: the contracting and GCGR checks each read their report row in an Excel.run
    const estimateResult = estimateId ? await fetchEstimateExport(estimateId) : null;
    const bidTabs = projectId && hasContracting ? await fetchBidTabsWithScopeData(projectId).catch(() => null) : null;
    const gcgrAvailability =
      projectId && gcgrId ? await fetchGCGRReportAvailability(projectId).catch(() => null) : null;
    // Gate Markups on the SAME base-bid markup line items the generator builds from (shares the
    // memoized export/processed-estimate), not the raw estimate_markups config — which also counts
    // alternate markups and would show the button for an estimate that generates an empty report.
    const markupItems = estimateId ? await fetchMarkupItemsForReport(estimateId).catch(() => []) : [];
    // TODO: Investigate in a follow up PR, if we can safely bring in promise.all into this function without breaking the Excel.run context.
    const [areaSheetExists, unitMixExists, roomsExist] = await reportTypesExist([
      AREA_SHEET_REPORT_NAME,
      UNIT_MIX_REPORT_NAME,
      ROOMS_REPORT_NAME,
    ]);

    const d = estimateResult?.success ? estimateResult.data : null;
    const ctx: VisibilityContext = {
      hasEstimateItems: (d?.line_items?.filter((i) => !i.alternate).length ?? 0) > 0,
      hasAlternateItems: (d?.line_items?.filter((i) => !!i.alternate && !i.parent).length ?? 0) > 0,
      hasMarkups: markupItems.length > 0,
      hasVMSummary: (d?.alternates?.length ?? 0) > 0,
      hasAreaSheet: (d?.area_matrix_items?.length ?? 0) > 0 && !areaSheetExists,
      hasBidTabs: (bidTabs?.length ?? 0) > 0,
      hasGCGRs: gcgrAvailability?.hasStaff ?? false,
      hasGCGRLineItems: gcgrAvailability?.hasLineItems ?? false,
      hasUnitMix: (d?.unit_types?.length ?? 0) > 0 && !unitMixExists,
      hasRooms: (d?.rooms?.length ?? 0) > 0 && !roomsExist,
    };

    const visible: Record<string, EdiphiReportTemplate> = {};
    for (const [key, template] of Object.entries(ediphiReportTemplates)) {
      try {
        if (template.renderIf(ctx)) {
          visible[key] = template;
        }
      } catch (error) {
        const err = handleError(error, { context: "getVisibleTemplates" });
        Logger.error("Errors occurred while determining visible report templates", err);
      }
    }
    return visible;
  };

  const generateTemplateAndReport = async (key: string, template: EdiphiReportTemplate): Promise<void> => {
    try {
      // Defence in depth — getVisibleTemplates already hides a template whose datasource is absent,
      // but a stale drawer could still fire one.
      const datasource = ediphiReportTemplates[key]?.group ?? "other";
      if (datasource === "estimate") {
        const estimateReport = await getEstimateReportData();
        if (!estimateReport[0]?.estimate_id) {
          throw new ValidationError("Load an Estimate to create a Report");
        }
      } else if (datasource === "gcgr") {
        if (!(await getLoadedGcgrId())) {
          throw new ValidationError("Load a GCGR to create a Report");
        }
      } else if (datasource === "contracting") {
        if (!(await getLoadedContractingProjectId())) {
          throw new ValidationError("Load Contracting data to create a Report");
        }
      }
      await setCalculationManual();
      await template.templateBuilder();
    } catch (error) {
      const err = handleError(error, { templateName: template.name, context: "generateTemplateAndReport" });
      Logger.error("Generate report template failed", err);
      throw err;
    }
  };

  return {
    withReportLogging,
    changeReportOption,
    checkReportDataStaleness,
    refreshReportData,
    getVisibleTemplates,
    generateTemplateAndReport,
  };
}
